import { Add, ArrowBack } from '@mui/icons-material';
import {
    Alert, Box, Button, FormControlLabel, Snackbar, Switch, TextField, Typography, styled,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { EmptyState } from 'src/components/common/EmptyState';
import { TitledAppBar } from 'src/components/common/TitledAppBar';
import { CONTENT_MAX_WIDTH } from 'src/components/SetsPlayground/constants';
import { useEditableSetsConfig } from 'src/hooks/useEditableSetsConfig';
import { SETS_CONFIG_SEARCH } from 'src/lib/routes';
import { deleteSetsConfig } from 'src/mutations/deleteSetsConfig';
import { mutateSetsConfig } from 'src/mutations/mutateSetsConfig';
import { QUERY_KEY as SETS_CONFIG_QUERY_KEY } from 'src/queries/querySetsConfig';
import { QUERY_KEY as SETS_CONFIGS_QUERY_KEY } from 'src/queries/querySetsConfigs';
import { getUserEmailQuery } from 'src/selectors/getUserEmailQuery';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { CopyFromProductModal } from './CopyFromProductModal';
import { CultureGroupPanel } from './CultureGroupPanel';
import { CultureGroupTabs } from './CultureGroupTabs';
import { SetsConfigDetailsFooter } from './SetsConfigDetailsFooter';
import { SetsConfigTopBarActions } from './SetsConfigTopBarActions';
import { MAX_DATE, MIN_DATE } from './dateOverlap';
import { syncInlineRankingExclusivity } from './inlineRankingExclusivity';
import { injectNewnessIntoSets, isReservedSetName } from './newnessSet';
import { groupHasValidationErrors } from './setValidation';

type ProductSetsConfig = Models.IngestConfigApi.SetsConfigs.ProductSetsConfig;
type SetsConfigCultureGroup = Models.IngestConfigApi.SetsConfigs.SetsConfigCultureGroup;

interface SetsConfigDetailsProps {
    initialConfig: ProductSetsConfig;
}

const toPersistedConfig = (
    config: ProductSetsConfig,
    modifiedBy: string,
): ProductSetsConfig => ({
    ...config,
    modifiedBy,
    cultureGroups: config.cultureGroups.map(({
        newnessEnabled, newnessLookbackDays, newnessDistribution, ...group
    }) => ({
        ...group,
        startDate: group.startDate ?? MIN_DATE,
        endDate: group.endDate ?? MAX_DATE,
        sets: injectNewnessIntoSets(group.sets.map(syncInlineRankingExclusivity), newnessEnabled, newnessLookbackDays, newnessDistribution),
    })) as SetsConfigCultureGroup[],
});

// Content is capped and centred at the same width the sets playground uses, so the two sets
// tools read the same and form fields keep a sane line length on wide monitors.
const StyledContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    margin: '0 auto',
    padding: `0 ${theme.spacing(4)}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    marginBottom: theme.spacing(16),
    // Inputs default to the same grey as the page, which makes them hard to pick out. Disabled
    // fields keep the default so they still read as not editable.
    '& .MuiOutlinedInput-root:not(.Mui-disabled)': {
        backgroundColor: theme.palette.common.white,
    },
}));

export const SetsConfigDetails = ({ initialConfig }: SetsConfigDetailsProps): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery);
    const userEmail = useRecoilValue(getUserEmailQuery);
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
    const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [copyModalOpen, setCopyModalOpen] = useState(false);
    const [copyInfoMessage, setCopyInfoMessage] = useState<string | null>(null);

    const {
        config,
        hasUnsavedChanges,
        setConfig,
        discardChanges,
        updateProductName,
        toggleIsEnabled,
        replaceCultureGroups,
        addGroup,
        removeGroup,
        updateGroupName,
        updateGroupCultures,
        updateGroupDates,
        toggleGroupNewness,
        updateGroupNewnessLookbackDays,
        updateGroupNewnessDistribution,
        updateGroupNotes,
        addSet,
        updateSet,
        removeSet,
    } = useEditableSetsConfig(initialConfig);

    // Clamp the selected tab whenever groups are added or removed so the index never points
    // past the end of the (possibly shorter) list.
    useEffect(() => {
        setSelectedGroupIndex((prev) => {
            if (config.cultureGroups.length === 0) {
                return 0;
            }

            return Math.min(prev, config.cultureGroups.length - 1);
        });
    }, [config.cultureGroups.length]);

    const duplicateNameIndexes = useMemo(() => {
        const indexesByName = new Map<string, number[]>();

        config.cultureGroups.forEach((group, index) => {
            const trimmed = group.name.trim();

            if (!trimmed) {
                return;
            }

            const indexes = indexesByName.get(trimmed) ?? [];

            indexes.push(index);
            indexesByName.set(trimmed, indexes);
        });

        const duplicates = new Set<number>();

        indexesByName.forEach((indexes) => {
            if (indexes.length > 1) {
                indexes.forEach((index) => duplicates.add(index));
            }
        });

        return duplicates;
    }, [config.cultureGroups]);

    const hasEmptyGroupName = config.cultureGroups.some((group) => group.name.trim() === '');
    const emptyCultureIndexes = useMemo(() => {
        const indexes = new Set<number>();

        config.cultureGroups.forEach((group, index) => {
            if (group.cultures.length === 0) {
                indexes.add(index);
            }
        });

        return indexes;
    }, [config.cultureGroups]);
    const hasEmptyName = config.productName.trim() === '';
    const hasReservedSetName = config.cultureGroups.some(
        (group) => group.sets.some((set) => isReservedSetName(set.name)),
    );
    const invalidGroupIndexes = useMemo(() => {
        const indexes = new Set<number>();

        config.cultureGroups.forEach((group, index) => {
            if (groupHasValidationErrors(group)) {
                indexes.add(index);
            }
        });

        return indexes;
    }, [config.cultureGroups]);
    const hasValidationError = duplicateNameIndexes.size > 0
        || emptyCultureIndexes.size > 0
        || hasEmptyGroupName
        || hasEmptyName
        || hasReservedSetName
        || invalidGroupIndexes.size > 0;

    const mutation = useMutation({
        mutationFn: mutateSetsConfig,
        onSuccess: (result) => {
            queryClient.invalidateQueries([SETS_CONFIGS_QUERY_KEY]);
            queryClient.invalidateQueries([SETS_CONFIG_QUERY_KEY]);
            // setConfig re-baselines both the live copy and the saved baseline to the server
            // result, which clears the dirty state.
            setConfig(result);
            setShowSuccessSnackbar(true);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteSetsConfig,
    });

    const handleAddGroup = (): void => {
        setSelectedGroupIndex(config.cultureGroups.length);
        addGroup();
    };

    const handleSave = async (): Promise<void> => {
        setSaveError(null);

        try {
            await mutation.mutateAsync({
                accessToken,
                productKey: config.productKey,
                config: toPersistedConfig(config, userEmail),
            });
        } catch (err) {
            setSaveError((err as Error).message);
        }
    };

    const handleCancel = (): void => {
        discardChanges();
        setSaveError(null);
    };

    const handleBack = (): void => {
        if (hasUnsavedChanges) {
            const confirmed = window.confirm('You have unsaved changes. Leave without saving?');

            if (confirmed) {
                navigate(SETS_CONFIG_SEARCH);
            }
        } else {
            navigate(SETS_CONFIG_SEARCH);
        }
    };

    const handleDelete = async (): Promise<void> => {
        await deleteMutation.mutateAsync({ accessToken, productKey: config.productKey });
        queryClient.invalidateQueries([SETS_CONFIGS_QUERY_KEY]);
        navigate(SETS_CONFIG_SEARCH);
    };

    const handleCopyFromProduct = (groups: SetsConfigCultureGroup[]): void => {
        replaceCultureGroups(groups);
        setSelectedGroupIndex(0);
        setCopyInfoMessage(`Copied ${groups.length} culture group${groups.length === 1 ? '' : 's'}. Review and save.`);
    };

    const activeGroup = config.cultureGroups[selectedGroupIndex];
    const previewSets = activeGroup
        ? injectNewnessIntoSets(
            activeGroup.sets.map(syncInlineRankingExclusivity),
            activeGroup.newnessEnabled,
            activeGroup.newnessLookbackDays,
            activeGroup.newnessDistribution,
        )
        : [];

    return (
        <>
            <TitledAppBar title={<Typography variant="h6">{`${config.productName} Sets Config`}</Typography>}>
                <SetsConfigTopBarActions
                    previewCultures={activeGroup?.cultures ?? []}
                    previewSets={previewSets}
                    productKey={config.productKey}
                    onCopyFromProduct={(): void => setCopyModalOpen(true)}
                    onDelete={handleDelete}
                />
            </TitledAppBar>
            <StyledContainer>
                <Button
                    color="secondary"
                    size="small"
                    startIcon={<ArrowBack />}
                    sx={{ justifySelf: 'flex-start', width: 'fit-content' }}
                    variant="text"
                    onClick={handleBack}
                >
                    Back to Sets Configs
                </Button>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <TextField
                        error={hasEmptyName}
                        helperText={hasEmptyName ? 'Name is required' : ' '}
                        label="Config name"
                        size="small"
                        sx={{ flex: 1, minWidth: 0 }}
                        value={config.productName}
                        onChange={(e): void => updateProductName(e.target.value)}
                    />
                    <TextField
                        disabled
                        label="Product key"
                        size="small"
                        sx={{ width: '160px', flexShrink: 0 }}
                        value={config.productKey}
                    />
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={config.isEnabled}
                                onChange={(e): void => toggleIsEnabled(e.target.checked)}
                            />
                        )}
                        label={config.isEnabled ? 'Enabled' : 'Disabled'}
                        labelPlacement="end"
                        sx={{ mt: 1, flexShrink: 0, whiteSpace: 'nowrap' }}
                    />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="subtitle2">
                        Culture groups
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                        Group the cultures that share the same sets, then add sets to each group. A
                        group only serves while its active window includes today.
                    </Typography>
                    <CultureGroupTabs
                        duplicateNameIndexes={duplicateNameIndexes}
                        groups={config.cultureGroups}
                        invalidGroupIndexes={invalidGroupIndexes}
                        selectedIndex={selectedGroupIndex}
                        onAddGroup={handleAddGroup}
                        onSelect={setSelectedGroupIndex}
                    />
                </Box>
                {activeGroup ? (
                    <CultureGroupPanel
                        cultureGroups={config.cultureGroups}
                        duplicateNameIndexes={duplicateNameIndexes}
                        emptyCultureIndexes={emptyCultureIndexes}
                        group={activeGroup}
                        groupIndex={selectedGroupIndex}
                        productKey={config.productKey}
                        onAddSet={addSet}
                        onRemoveGroup={removeGroup}
                        onRemoveSet={removeSet}
                        onToggleNewness={toggleGroupNewness}
                        onUpdateCultures={updateGroupCultures}
                        onUpdateDates={updateGroupDates}
                        onUpdateName={updateGroupName}
                        onUpdateNewnessDistribution={updateGroupNewnessDistribution}
                        onUpdateNewnessLookbackDays={updateGroupNewnessLookbackDays}
                        onUpdateNotes={updateGroupNotes}
                        onUpdateSet={updateSet}
                    />
                ) : (
                    <EmptyState>
                        <Typography gutterBottom align="center" variant="h6">
                            No culture groups yet
                        </Typography>
                        <Button startIcon={<Add />} sx={{ mt: 2 }} variant="contained" onClick={handleAddGroup}>
                            Add culture group
                        </Button>
                    </EmptyState>
                )}
            </StyledContainer>
            <SetsConfigDetailsFooter
                config={config}
                error={saveError}
                hasUnsavedChanges={hasUnsavedChanges}
                hasValidationError={hasValidationError}
                isSaving={mutation.isLoading}
                onCancel={handleCancel}
                onSave={handleSave}
            />
            <CopyFromProductModal
                currentProductKey={config.productKey}
                open={copyModalOpen}
                onClose={(): void => setCopyModalOpen(false)}
                onCopy={handleCopyFromProduct}
            />
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={3000}
                open={showSuccessSnackbar}
                onClose={(): void => setShowSuccessSnackbar(false)}
            >
                <Alert severity="success" variant="filled">
                    Sets config saved successfully
                </Alert>
            </Snackbar>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={4000}
                open={!!copyInfoMessage}
                onClose={(): void => setCopyInfoMessage(null)}
            >
                <Alert severity="info" variant="filled">
                    {copyInfoMessage}
                </Alert>
            </Snackbar>
        </>
    );
};
